import React from 'react';
import {
  MailboxDraftFieldsFragment,
  MailboxDraftReconciliation,
  ReconcileMailboxDraftsMutation,
  useReconcileMailboxDraftsMutation
} from '~/api/graphql';
import { OutboxDraftPatch } from '~/components/mailbox/outbox-rows';

/**
 * What one look at a draft concluded. These are the mailbox runtime's own
 * outcomes, so a verdict lands on a row without a vocabulary in between:
 * `sentViaCara`, `sentExternally` and `goneUnproven` are the three retirement
 * reasons the row model already renders, and the other five are all the ways a
 * look can end with the ledger untouched.
 *
 * `unreachable` and `notReached` are not verdicts about the draft at all. They
 * say the mailbox refused to answer, or that the pass ran out of budget before
 * this draft's turn — and a row must stop waiting on either, because neither is
 * going to resolve on its own.
 *
 * `mailboxDisconnected` is the third of those, and worth telling apart because
 * it names its own cause: syncing again is the remedy for the other two and no
 * remedy at all for this one, which needs the mailbox reconnected before any
 * pass can get further.
 */
export type MailboxDraftOutcome =
  | 'unchanged'
  | 'sentViaCara'
  | 'sentExternally'
  | 'goneUnproven'
  | 'deferred'
  | 'unreachable'
  | 'mailboxDisconnected'
  | 'notReached';

/**
 * One draft's answer. `draft` is the row as the ledger holds it where the pass
 * rewrote it.
 */
export interface MailboxDraftVerdict {
  draftId: string;
  outcome: MailboxDraftOutcome;
  draft?: Pick<
    MailboxDraftFieldsFragment,
    'id' | 'webLink' | 'sentViaCaraAt' | 'retiredAt' | 'retiredReason'
  >;
}

/**
 * The single call the outbox makes to re-read parked drafts against the
 * provider.
 *
 * Every draft named comes back with something to say about it, including the
 * two outcomes that mean nobody managed to ask, so the caller never has to read
 * an id's absence as agreement.
 *
 * The signal is not optional: the operation talks to a third party over a
 * connection Cara does not control, so every caller must be able to give up.
 */
export type ReconcileMailboxDrafts = (
  draftIds: string[],
  options: { signal: AbortSignal }
) => Promise<MailboxDraftVerdict[]>;

const RECONCILIATION_OUTCOMES: Record<MailboxDraftReconciliation, MailboxDraftOutcome> = {
  [MailboxDraftReconciliation.Unchanged]: 'unchanged',
  [MailboxDraftReconciliation.SentViaCara]: 'sentViaCara',
  [MailboxDraftReconciliation.SentExternally]: 'sentExternally',
  [MailboxDraftReconciliation.GoneUnproven]: 'goneUnproven',
  [MailboxDraftReconciliation.Deferred]: 'deferred',
  [MailboxDraftReconciliation.Unreachable]: 'unreachable',
  [MailboxDraftReconciliation.MailboxDisconnected]: 'mailboxDisconnected',
  [MailboxDraftReconciliation.NotReached]: 'notReached'
};

/**
 * The operation's results as the page reads them. A reconciliation this build
 * has no name for is treated as a look that reached nothing, which is the
 * reading that leaves the ledger's own account of the row standing.
 */
export const mailboxDraftVerdicts = (
  results: ReconcileMailboxDraftsMutation['reconcileMailboxDrafts']
): MailboxDraftVerdict[] =>
  results.map((result) => ({
    draftId: result.mailboxDraftId,
    outcome: RECONCILIATION_OUTCOMES[result.reconciliation] ?? 'notReached',
    draft: result.draft
  }));

/**
 * The operation, as the hook below wants it.
 *
 * The refusal has to be raised rather than returned: the client asks with
 * `errorPolicy: 'all'`, so a batch the server would not accept — one over the
 * per-pass cap, or naming a draft the caller may not read — resolves with the
 * errors and no data. Returning that as an empty list would read as every draft
 * having been asked about and none having anything to say.
 */
export function useReconcileMailboxDrafts(): ReconcileMailboxDrafts {
  const [reconcileMailboxDrafts] = useReconcileMailboxDraftsMutation();

  return React.useCallback(
    async (draftIds, { signal }) => {
      const result = await reconcileMailboxDrafts({
        variables: { mailboxDraftIds: draftIds },
        context: { fetchOptions: { signal } }
      });
      if (!result.data) {
        throw new Error(result.errors?.[0]?.message ?? 'reconcileMailboxDrafts returned no data');
      }
      return mailboxDraftVerdicts(result.data.reconcileMailboxDrafts);
    },
    [reconcileMailboxDrafts]
  );
}

/**
 * How many drafts go in one request, matching the runtime's own per-pass cap.
 * An oversized list is refused whole rather than truncated, so chunking to this
 * is the caller's job.
 */
export const RECONCILE_BATCH_SIZE = 50;

/**
 * How long a batch may run before its rows stop waiting. Set past the runtime's
 * own 20-second pass budget so that a slow pass returns its partial answers
 * rather than being cut off by the client that asked for them — but not so far
 * past that a provider which has simply stopped answering leaves a row spinning
 * for as long as the tab is open.
 */
export const RECONCILE_TIMEOUT_MS = 25_000;

export function chunkDraftIds(ids: string[], size: number): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

/**
 * The verdict as a change to the row, or null where the look established
 * nothing. Only the three settled outcomes rewrite anything; the rest leave the
 * row reading exactly as the ledger last recorded it, which is the honest
 * answer when the mailbox would not say.
 *
 * A pass that rewrote the ledger returns the row it wrote, and that is
 * preferred: `retiredAt` records when Cara noticed, and the server noticed.
 * Falling back to the moment the answer arrived keeps a row from reading as
 * live when the provider has just said otherwise.
 */
export function outcomeToDraftPatch(
  verdict: MailboxDraftVerdict,
  noticedAt: Date
): OutboxDraftPatch | null {
  if (verdict.draft) {
    const { webLink, sentViaCaraAt, retiredAt, retiredReason } = verdict.draft;
    return { webLink, sentViaCaraAt, retiredAt, retiredReason };
  }
  switch (verdict.outcome) {
    case 'sentViaCara':
      return { retiredAt: noticedAt, retiredReason: 'sent_via_cara' };
    case 'sentExternally':
      return { retiredAt: noticedAt, retiredReason: 'sent_externally' };
    case 'goneUnproven':
      return { retiredAt: noticedAt, retiredReason: 'deleted_externally' };
    default:
      return null;
  }
}

export interface MailboxDraftReconciliationState {
  /** Drafts whose answer is outstanding. Their rows show a spinner in place of a status. */
  checkingDraftIds: ReadonlySet<string>;
  /**
   * Drafts whose mailbox cannot be asked until somebody reconnects it. Their
   * rows say so instead of falling back to the ledger's `inMailbox`, which
   * would have them waiting on a mailbox nobody can see into.
   *
   * Not carried in `patches`: nothing about the draft row changed, and a patch
   * is what reconciliation is allowed to rewrite about it.
   */
  disconnectedDraftIds: ReadonlySet<string>;
  patches: ReadonlyMap<string, OutboxDraftPatch>;
}

const EMPTY_STATE: MailboxDraftReconciliationState = {
  checkingDraftIds: new Set(),
  disconnectedDraftIds: new Set(),
  patches: new Map()
};

/**
 * Re-reads parked drafts against the provider and reports each answer as it
 * arrives.
 *
 * Every id is asked about once per mount, so paging further into the list
 * checks the rows that page brought and leaves the rows above it alone. There
 * is no path on which a batch leaves its rows checking: a verdict, an outcome
 * saying nobody managed to ask, a rejected call and a timeout all end the same
 * way, by clearing the whole batch.
 *
 * Reconciliation is an enhancement, not a prerequisite — with no `reconcile`
 * supplied the hook does nothing and every row shows the status the ledger
 * already knows.
 */
export function useMailboxDraftReconciliation(opts: {
  draftIds: string[];
  reconcile?: ReconcileMailboxDrafts;
}): MailboxDraftReconciliationState {
  const { draftIds, reconcile } = opts;

  const [state, setState] = React.useState<MailboxDraftReconciliationState>(EMPTY_STATE);
  const askedRef = React.useRef(new Set<string>());
  const draftIdsRef = React.useRef(draftIds);
  draftIdsRef.current = draftIds;

  // Batches outlive the effect that started them: paging in more rows changes
  // the id list, and re-running the effect must not cancel the requests already
  // in flight for the rows above. Only leaving the page does.
  const controllersRef = React.useRef(new Set<AbortController>());
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    const controllers = controllersRef.current;
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      for (const controller of controllers) {
        controller.abort();
      }
      controllers.clear();
    };
  }, []);

  // The ids arrive as a fresh array every render, so the effect keys off their
  // contents rather than the array's identity.
  const draftIdsKey = draftIds.join(',');

  React.useEffect(() => {
    if (!reconcile) {
      return;
    }
    const pending = draftIdsRef.current.filter((id) => !askedRef.current.has(id));
    if (pending.length === 0) {
      return;
    }
    for (const id of pending) {
      askedRef.current.add(id);
    }

    setState((current) => ({
      ...current,
      checkingDraftIds: new Set([...current.checkingDraftIds, ...pending])
    }));

    for (const batch of chunkDraftIds(pending, RECONCILE_BATCH_SIZE)) {
      const controller = new AbortController();
      controllersRef.current.add(controller);
      const timeoutId = window.setTimeout(() => controller.abort(), RECONCILE_TIMEOUT_MS);

      void reconcile(batch, { signal: controller.signal })
        .then((verdicts) => {
          if (!mountedRef.current) {
            return;
          }
          const noticedAt = new Date();
          setState((current) => {
            const patches = new Map(current.patches);
            const disconnectedDraftIds = new Set(current.disconnectedDraftIds);
            for (const verdict of verdicts) {
              const patch = outcomeToDraftPatch(verdict, noticedAt);
              if (patch) {
                patches.set(verdict.draftId, patch);
              }
              // A reconnect between two passes is answered by the second, so
              // the id is dropped as readily as it is added.
              if (verdict.outcome === 'mailboxDisconnected') {
                disconnectedDraftIds.add(verdict.draftId);
              } else {
                disconnectedDraftIds.delete(verdict.draftId);
              }
            }
            return { ...current, patches, disconnectedDraftIds };
          });
        })
        .catch(() => {
          // A batch that failed or timed out changes nothing on screen. Its
          // rows fall back to their last-known status below.
        })
        .finally(() => {
          window.clearTimeout(timeoutId);
          controllersRef.current.delete(controller);
          if (!mountedRef.current) {
            return;
          }
          setState((current) => {
            const checkingDraftIds = new Set(current.checkingDraftIds);
            for (const id of batch) {
              checkingDraftIds.delete(id);
            }
            return { ...current, checkingDraftIds };
          });
        });
    }
  }, [draftIdsKey, reconcile]);

  return state;
}
